import React, { useState, ReactNode } from "react";
import { Icon } from "@ugitgud/legos/ui/icon/Icon";

// Icons
import { SvgYearInReviewSupport } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewSupport";
import { SvgYearInReviewJungle } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewJungle";
import { SvgYearInReviewGold } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewGold";
import { SvgYearInReviewKills } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewKills";
import { SvgYearInReviewKda } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewKda";
import { SvgYearInReviewAssists } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewAssists";
import { SvgYearInReviewCs } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewCs";
import { getYirSVG, srRoles, RoleS } from "@ugg/shared/utils/role-helpers";
import { availableTiersAsc, getRankSvg } from "@ugg/shared/utils/rank-helpers";
import classnames from "classnames";

// --- Types ---

export type StatKey = "kda" | "csMin" | "goldMin" | "wardsGame" | "dmgMitigated" | "healing" | "dmgMin";

export type PlayerStats = Partial<Record<RoleS, Record<StatKey, number>>>;
export type RankStats = Array<PlayerStats>;

interface RoleBreakdownModuleProps {
  playerStats: PlayerStats;
  rankData: RankStats | null;
}

interface CompareStatsBlockProps {
  icon: ReactNode;
  stat: string;
  playerStat: number;
  rankAvgStat: number;
}

interface RoleButtonProps {
  role: RoleS;
  isActive: boolean;
  onClick: () => void;
}

interface RoleButtonContainerProps {
  currentRole: RoleS;
  onRoleSelect: (role: RoleS) => void;
}

interface SliderBlockProps {
  value: number;
  onChange: (val: number) => void;
}

const CUSTOM_PERCENTAGES = [0, 14, 24.4, 34.8, 45.2, 55.7, 66, 76.1, 86.5, 100];

const STAT_ROWS = [
  { key: "kda" as StatKey, label: "KDA", icon: <SvgYearInReviewKda /> },
  { key: "csMin" as StatKey, label: "CS / Min", icon: <SvgYearInReviewCs /> },
  { key: "goldMin" as StatKey, label: "Gold / Min", icon: <SvgYearInReviewGold /> },
  { key: "wardsGame" as StatKey, label: "Wards / Game", icon: <SvgYearInReviewSupport /> },
  { key: "dmgMitigated" as StatKey, label: "Damage Mitigated", icon: <SvgYearInReviewAssists /> },
  { key: "healing" as StatKey, label: "Avg. Healing", icon: <SvgYearInReviewJungle /> },
  { key: "dmgMin" as StatKey, label: "Damage / Min", icon: <SvgYearInReviewKills /> },
];

const BTN_ACTIVE = "bg-[#9A7D46] shadow-[inset_0_1px_0_0_#F4C874]";
const BTN_INACTIVE = "bg-[#25254B] shadow-[inset_0_1px_0_0_#575792]";
const BTN_COMMON =
  "drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)] xs:h-[60px] xs:w-[60px] h-[38px] w-[38px] shrink-0 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer";

function RoleButton({ role, isActive, onClick }: RoleButtonProps) {
  return (
    <button
      className={classnames(BTN_COMMON, {
        [BTN_ACTIVE]: isActive,
        [BTN_INACTIVE]: !isActive,
      })}
      onClick={onClick}
    >
      {getYirSVG(role, isActive, "xs:w-[30px] xs:h-[30px] w-[19px] h-[19px]")}
    </button>
  );
}

function RoleButtonContainer({ currentRole, onRoleSelect }: RoleButtonContainerProps) {
  return (
    <div className="flex justify-center gap-[30px] xs:gap-12">
      {srRoles.map((role) => (
        <RoleButton key={role} role={role} isActive={currentRole === role} onClick={() => onRoleSelect(role)} />
      ))}
    </div>
  );
}

const StatBar = ({ percentage, value, colorClass }: { percentage: number; value: number; colorClass: string }) => (
  <div className="relative w-full h-[4px]">
    <div
      className={`absolute top-1/2 -translate-y-1/2 h-[4px] ${colorClass} rounded-[4px] transition-all duration-500 ease-out`}
      style={{ width: `${percentage}%` }}
    />
    <div
      className="absolute top-1/2 -translate-y-1/2 text-[#CDDCFE] font-['Barlow'] text-sm italic font-medium leading-normal ml-[12px] transition-all duration-500 ease-out "
      style={{ left: `${percentage}%` }}
    >
      {value}
    </div>
  </div>
);

const CompareStatsBlock: React.FC<CompareStatsBlockProps> = ({ icon, rankAvgStat, playerStat, stat }) => {
  const baseline = Math.max(playerStat, rankAvgStat, 1);
  const playerPercentage = (playerStat / baseline) * 100;
  const rankPercentage = (rankAvgStat / baseline) * 100;

  const isLastItem = stat === "Damage / Min";

  return (
    <div className="flex- flex-col">
      <div className="flex w-full justify-center items-center gap-5">
        <Icon icon={icon} className="text-[#383864] w-[24px] h-[24px]" />
        <div className="flex flex-col justify-center w-full gap-3 pr-[55px]">
          <div className="text-white font-['Barlow'] text-[20px] font-medium leading-normal">{stat}</div>
          <div className="flex flex-col gap-[12px]">
            <StatBar percentage={playerPercentage} value={playerStat} colorClass="bg-[#F4C874]" />
            <StatBar percentage={rankPercentage} value={rankAvgStat} colorClass="bg-[#3273FA]" />
          </div>
        </div>
      </div>
      {!isLastItem && <div className="xs:w-[498px] w-[348px] h-px shrink-0 bg-[#11112A] mt-5" />}
    </div>
  );
};

function SliderBlock({ value, onChange }: SliderBlockProps) {
  const TOTAL_STEPS = availableTiersAsc.length;
  const MAX_VAL = TOTAL_STEPS - 1;
  const fillPercent = CUSTOM_PERCENTAGES[value];

  return (
    <div className="flex flex-col xs:w-[500px] w-[350px]">
      {/* Legend */}
      <div className="flex items-center justify-center">
        <div className="text-[#CDDCFE] font-inter text-xs font-normal leading-normal mr-[33px]">Choose a rank to compare to:</div>
        <div className="w-2 h-2 rounded-full bg-[#F4C874]" />
        <div className="text-[#CDDCFE] font-inter text-xs font-normal leading-normal ml-[4px] mr-[18px]">You</div>
        <div className="w-2 h-2 rounded-full bg-[#3273FA]" />
        <div className="text-[#CDDCFE] font-inter text-xs font-normal leading-normal ml-[4px]">
          The rank she told you not to worry about
        </div>
      </div>

      {/* Slider Input & Track */}
      <div className="relative w-full h-[12px] flex items-center mt-[23px]">
        <input
          type="range"
          min={0}
          max={MAX_VAL}
          step={1}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value))}
          className="w-full absolute z-20 opacity-0 cursor-pointer h-full"
        />
        <div className="w-full h-[2px] bg-[#383864] rounded-full relative z-10 pointer-events-none">
          <div
            className="h-full bg-[#3273FA] rounded-full transition-all duration-150 ease-out"
            style={{ width: `${fillPercent}%` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 bg-[#3273FA] rounded-full transition-all duration-150 ease-out shadow-[0_0_10px_rgba(50,115,250,0.4)] h-3 w-3"
            style={{
              left: `${fillPercent}%`,
            }}
          />
        </div>
      </div>

      {/* Icons */}
      <div className="flex w-full justify-between items-center mt-3 mx-[3px]">
        {availableTiersAsc.map((tier, index) => {
          return (
            <div
              key={`${tier}`}
              onClick={() => onChange(index)}
              className={`flex flex-col items-center cursor-pointer transition-all duration-300 : ''}`}
            >
              <Icon icon={getRankSvg(tier)} className="xs:w-[30px] xs:h-[30px] w-[19px] h-[19px]" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RoleBreakdownModule({ playerStats, rankData }: RoleBreakdownModuleProps) {
  const initialActiveRole = srRoles.find((role) => playerStats[role]) || RoleS.TOP;
  const [activeRole, setActiveRole] = useState<RoleS>(initialActiveRole);
  const [sliderValue, setSliderValue] = useState(4);

  if (!rankData || rankData.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 text-white">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
        <p>Loading Rank Data...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-[60px] xs:w-[500px] w-[350px]">
      <SliderBlock value={sliderValue} onChange={setSliderValue} />

      <RoleButtonContainer currentRole={activeRole} onRoleSelect={setActiveRole} />

      <div className="flex flex-col w-full gap-[14px]">
        {STAT_ROWS.map((row, index) => {
          const playerValue = playerStats[activeRole]?.[row.key] ?? 0;
          const rankValue = rankData[sliderValue][activeRole]?.[row.key] ?? 0;

          return (
            <React.Fragment key={row.key}>
              <CompareStatsBlock
                stat={row.label}
                icon={row.icon}
                playerStat={Number(playerValue.toFixed(1))}
                rankAvgStat={Number(rankValue.toFixed(1))}
              />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
